import React, { useState, useEffect } from 'react';
import {
  Table, Button, Space, Modal, Form, Input, message, 
  Card, Tag, Checkbox, Row, Col, Divider, Switch
} from 'antd';
import {
  EditOutlined, DeleteOutlined, PlusOutlined, SafetyOutlined, CheckSquareOutlined, SettingOutlined
} from '@ant-design/icons';
import { perfilService } from '../services/api';
import PerfilPermissoesEditor from './PerfilPermissoesEditor';

const PerfisList = () => {
  const [perfis, setPerfis] = useState([]);
  const [menuItens, setMenuItens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPermissoesModalVisible, setIsPermissoesModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [perfilSelecionado, setPerfilSelecionado] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPerfis();
    fetchMenuItens();
  }, []);

  const fetchPerfis = async () => {
    setLoading(true);
    try {
      const response = await perfilService.list();
      setPerfis(response.data);
    } catch (error) {
      message.error('Erro ao carregar perfis');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchMenuItens = async () => {
    try {
      const response = await perfilService.listMenuItens();
      setMenuItens(response.data);
    } catch (error) {
      message.error('Erro ao carregar itens do menu');
      console.error(error);
    }
  };

  const handleAdd = () => {
    form.resetFields();
    form.setFieldsValue({ ativo: true, menu_itens_ids: [] });
    setEditingId(null);
    setIsModalVisible(true);
  };

  const handleEdit = async (record) => {
    try {
      const response = await perfilService.get(record.id);
      const perfil = response.data;
      
      setEditingId(record.id);
      form.setFieldsValue({
        nome: perfil.nome,
        descricao: perfil.descricao,
        ativo: perfil.ativo,
        menu_itens_ids: perfil.menu_itens.map(m => m.id)
      });
      setIsModalVisible(true);
    } catch (error) {
      message.error('Erro ao carregar dados do perfil');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Deseja excluir este perfil? Não será possível excluir se houver usuários usando este perfil.',
      okText: 'Sim',
      cancelText: 'Não',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await perfilService.delete(id);
          message.success('Perfil excluído com sucesso');
          fetchPerfis();
        } catch (error) {
          if (error.response?.status === 400) {
            message.error(error.response.data.detail || 'Não é possível excluir este perfil');
          } else {
            message.error('Erro ao excluir perfil');
          }
        }
      },
    });
  };

  const handleGerenciarPermissoes = (record) => {
    setPerfilSelecionado(record);
    setIsPermissoesModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (editingId) {
        await perfilService.update(editingId, values);
        message.success('Perfil atualizado com sucesso');
      } else {
        await perfilService.create(values);
        message.success('Perfil criado com sucesso');
      }
      setIsModalVisible(false);
      fetchPerfis();
    } catch (error) {
      if (error.response?.status === 400) {
        message.error(error.response.data.detail || 'Erro ao salvar perfil');
      } else {
        message.error('Erro ao salvar perfil');
      }
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
      render: (text) => (
        <Space>
          <SafetyOutlined style={{ color: '#1890ff' }} />
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
      ellipsis: true,
    },
    {
      title: 'Permissões',
      dataIndex: 'total_permissoes',
      key: 'total_permissoes',
      width: 120,
      align: 'center',
      render: (total) => (
        <Tag color="blue" icon={<CheckSquareOutlined />}>
          {total} {total === 1 ? 'item' : 'itens'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'ativo',
      key: 'ativo',
      width: 100,
      align: 'center',
      render: (ativo) => (
        <Tag color={ativo ? 'green' : 'red'}>
          {ativo ? 'Ativo' : 'Inativo'}
        </Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 180,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<SettingOutlined />}
            onClick={() => handleGerenciarPermissoes(record)}
            title="Gerenciar Permissões Detalhadas"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Editar Perfil"
          />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            title="Excluir Perfil"
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <SafetyOutlined style={{ fontSize: '20px' }} />
            <span>Gerenciar Perfis</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Novo Perfil
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={perfis}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} perfis`,
          }}
        />
      </Card>

      <Modal
        title={editingId ? 'Editar Perfil' : 'Novo Perfil'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        width={800}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item
                name="nome"
                label="Nome do Perfil"
                rules={[
                  { required: true, message: 'Nome obrigatório' },
                  { max: 50, message: 'Máximo 50 caracteres' }
                ]}
              >
                <Input placeholder="Ex: GERENTE, SUPERVISOR" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="ativo"
                label="Status"
                valuePropName="checked"
              >
                <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="descricao"
            label="Descrição"
            rules={[{ max: 200, message: 'Máximo 200 caracteres' }]}
          >
            <Input.TextArea 
              rows={3}
              placeholder="Descreva as responsabilidades deste perfil..."
            />
          </Form.Item>

          <Divider orientation="left">
            <Space>
              <CheckSquareOutlined />
              Permissões de Menu
            </Space>
          </Divider>

          <Form.Item
            name="menu_itens_ids"
            label="Selecione os itens do menu que este perfil poderá acessar:"
          >
            <Checkbox.Group style={{ width: '100%' }}>
              <Row gutter={[16, 16]}>
                {menuItens.map((item) => (
                  <Col xs={24} sm={12} md={8} key={item.id}>
                    <Checkbox value={item.id} style={{ width: '100%' }}>
                      <Space>
                        <Tag color="blue">{item.ordem}</Tag>
                        {item.nome}
                      </Space>
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            background: '#f5f5f5', 
            borderRadius: '4px' 
          }}>
            <small>
              <strong>Dica:</strong> Selecione cuidadosamente as permissões. 
              Usuários com este perfil terão acesso apenas aos itens marcados.
            </small>
          </div>
        </Form>
      </Modal>

      <Modal
        title={null}
        open={isPermissoesModalVisible}
        onCancel={() => setIsPermissoesModalVisible(false)}
        footer={null}
        width="95%"
        style={{ top: 20 }}
        destroyOnClose
      >
        {perfilSelecionado && (
          <PerfilPermissoesEditor 
            perfilId={perfilSelecionado.id}
            perfilNome={perfilSelecionado.nome}
          />
        )}
      </Modal>
    </div>
  );
};

export default PerfisList;
